import { CheckOutlined, FileTextOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Divider, Flex, Form, Radio, RadioChangeEvent, Row, Select, Space, Table, Tooltip } from "antd";
import dayjs from "dayjs";
import * as PhosphorIcon from "phosphor-react";
import React, { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import { Vm_ContaBancoModel } from "../../../../models/contaBanco/vm_contaBancoModel";
import { PagamentoLoteJsonModel } from "../../../../models/pagamentoLote/pagamentoLoteJsonModel";
import { PagamentoLoteModel } from "../../../../models/pagamentoLote/pagamentoLoteModel";
import { setTitle } from "../../../../redux/slices/configuracoes/configuracoesSlice";
import { setPageContaFinanceira } from "../../../../redux/slices/contaFinanceira/contaFinanceiraSlice";
import { setPageLancamentoBancario } from "../../../../redux/slices/lancamentoBancario/lancamentoBancarioSlice";
import { buscarContaBanco } from "../../../../services/contaBancoService";
import { buscarFormaPagamentoRecebimento } from "../../../../services/formaPagamentoRecebimentoService";
import { cadastrarPagamentoLote } from "../../../../services/pagamentoLoteService";
import { currencyBRL, realParaNumber } from "../../../../util/dinheiroBRAuxiliar";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";

dayjs.locale("pt-br");

interface Item {
  idContaFinanceira: number;
  numeroDocumento: string | null;
  dataVencimento: Date | null;
  apelidoFavorecido: string | null;
  valorLiquido: number | null;
  valor: number;
  saldo: number;
  juros: number;
  multa: number;
  outrosValores: number;
  desconto: number;
  historico: string;
  valorTotal: number;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "valor" | "juros" | "multa" | "outrosValores" | "desconto";
  record: PagamentoLoteJsonModel;
  index: number;
  children: React.ReactNode;
}

const PagamentosPage = () => {
  //estados globais
  const contaFinanceiraState = useAppSelector((state) => state.contaFinanceira);
  const contaBancoState = useAppSelector((state) => state.contaBanco);
  const formaPagamentoRecebimentoState = useAppSelector((state) => state.formaPagamentoRecebimento);
  const persistedState = useAppSelector((state) => state.persisted);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm();
  const [formManual] = Form.useForm();
  const [editingKey, setEditingKey] = useState<number>(0);
  const [totalItens, setTotalItens] = useState<number>(0);
  const [totalSaldo, setTotalSaldo] = useState<number>(0);
  const [dataSource, setDataSource] = useState<Item[]>([]);

  const [valueRadioTipoPagamento, setValueTipoPagamento] = useState(true);
  const radioOptionsTipoPagamento = [
    { label: "Lote", value: false, disabled: true },
    { label: "Individual", value: true },
  ];

  const pagar = contaFinanceiraState.origemCadastro === "contaFinanceira" && contaFinanceiraState.tipoCadastro === "despesa";
  const lancamentoDespesa = contaFinanceiraState.origemCadastro === "lancamento" && contaFinanceiraState.tipoCadastro === "despesa";

  useEffect(() => {
    if (persistedState.user.empresa !== null || (persistedState.user.empresas !== null && persistedState.user.empresas!.length > 0)) {
      const minIdEmpresa = Math.min(...persistedState.user.empresas!.map((emp) => emp.idEmpresa)); // Pega as PROMOBEM
      dispatch(buscarContaBanco({ pesquisar: null, id: null, idEmpresa: persistedState.user.empresa?.idEmpresa ?? minIdEmpresa }));
    } else {
      dispatch(buscarContaBanco(contaBancoState.busca));
    }

    dispatch(buscarFormaPagamentoRecebimento(formaPagamentoRecebimentoState.busca));
    var initialData: Item[] = [];

    if (contaFinanceiraState.origemCadastro === "lancamento") {
      dispatch(setTitle("REALIZAR CONTA"));
      form.setFieldValue("idContaBanco", contaFinanceiraState.entity.idContaBanco);
      initialData.push({
        idContaFinanceira: contaFinanceiraState.entity.id,
        numeroDocumento: contaFinanceiraState.entity.numeroDocumento,
        dataVencimento: contaFinanceiraState.entity.dataVencimento,
        apelidoFavorecido: contaFinanceiraState.entity.apelidoFavorecido,
        valorLiquido: contaFinanceiraState.entity.valorLiquido,
        valor: contaFinanceiraState.entity.valorLiquido - contaFinanceiraState.entity.valorPago,
        saldo: contaFinanceiraState.entity.valorLiquido - contaFinanceiraState.entity.valorPago,
        juros: 0,
        multa: 0,
        outrosValores: 0,
        desconto: 0,
        historico: contaFinanceiraState.entity.historico,
        valorTotal: contaFinanceiraState.entity.valorLiquido - contaFinanceiraState.entity.valorPago,
      });
    } else {
      dispatch(setTitle(pagar || contaFinanceiraState.origemCadastro === "guia" || lancamentoDespesa || lancamentoDespesa ? "PAGAR CONTAS" : "RECEBER CONTAS"));
      form.setFieldValue("idContaBanco", contaFinanceiraState.selectedRowKeys.rows[0].idContaBanco);
      for (var i = 0; i < contaFinanceiraState.selectedRowKeys.rows.length; i++) {
        initialData.push({
          idContaFinanceira: contaFinanceiraState.selectedRowKeys.rows[i].id,
          numeroDocumento: contaFinanceiraState.selectedRowKeys.rows[i].numeroDocumento,
          dataVencimento: contaFinanceiraState.selectedRowKeys.rows[i].dataVencimento,
          apelidoFavorecido: contaFinanceiraState.selectedRowKeys.rows[i].apelidoFavorecido,
          valorLiquido: contaFinanceiraState.selectedRowKeys.rows[i].valorLiquido,
          valor: contaFinanceiraState.selectedRowKeys.rows[i].valorLiquido - contaFinanceiraState.selectedRowKeys.rows[i].valorPago,
          saldo: contaFinanceiraState.selectedRowKeys.rows[i].valorLiquido - contaFinanceiraState.selectedRowKeys.rows[i].valorPago,
          juros: 0,
          multa: 0,
          outrosValores: 0,
          desconto: 0,
          historico: contaFinanceiraState.selectedRowKeys.rows[i].historico,
          valorTotal: contaFinanceiraState.selectedRowKeys.rows[i].valorLiquido - contaFinanceiraState.selectedRowKeys.rows[i].valorPago,
        });
      }
    }

    setDataSource(initialData);

    let soma = initialData.reduce((prev, current) => {
      const valorTotal = current.saldo ? realParaNumber(current.saldo.toString()) : 0;
      return prev + valorTotal;
    }, 0);
    setTotalItens(soma);

    soma = initialData.reduce((prev, current) => {
      const valorTotal = current.saldo ? realParaNumber(current.saldo.toString()) : 0;
      return prev + valorTotal;
    }, 0);
    setTotalSaldo(soma);
  }, []);

  const onChangeRadioTipoPagamento = async ({ target: { value } }: RadioChangeEvent) => {
    setValueTipoPagamento(value);
  };

  //Início Funções tabela centro de custo
  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({ editing, dataIndex, title, inputType, record, index, children, ...restProps }) => {
    const inputNode =
      inputType === "valor" ? (
        <NumericFormat
          className="ant-input noBoderColor number-input"
          style={{ width: 100, fontSize: 11, padding: 3, minHeight: 24 }}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix=""
          fixedDecimalScale={true}
          onValueChange={(e) => {
            formManual.setFieldValue("saldo", e.floatValue);
          }}
        />
      ) : inputType === "juros" ? (
        <NumericFormat
          className="ant-input noBoderColor number-input"
          style={{ width: 70, fontSize: 11, padding: 3, minHeight: 24 }}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix=""
          fixedDecimalScale={true}
          onValueChange={(e) => {
            formManual.setFieldValue("juros", e.floatValue);
          }}
        />
      ) : inputType === "multa" ? (
        <NumericFormat
          className="ant-input noBoderColor number-input"
          style={{ width: 70, fontSize: 11, padding: 3, minHeight: 24 }}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix=""
          fixedDecimalScale={true}
          onValueChange={(e) => {
            formManual.setFieldValue("multa", e.floatValue);
          }}
        />
      ) : inputType === "outrosValores" ? (
        <NumericFormat
          className="ant-input noBoderColor number-input"
          style={{ width: 70, fontSize: 11, padding: 3, minHeight: 24 }}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix=""
          fixedDecimalScale={true}
          onValueChange={(e) => {
            formManual.setFieldValue("outrosValores", e.floatValue);
          }}
        />
      ) : (
        <NumericFormat
          className="ant-input noBoderColor number-input"
          style={{ width: 70, fontSize: 11, padding: 3, minHeight: 24 }}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix=""
          fixedDecimalScale={true}
          onValueChange={(e) => {
            formManual.setFieldValue("desconto", e.floatValue);
          }}
        />
      );
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Campo obrigatório`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const isEditing = (record: Item) => record.idContaFinanceira === editingKey;

  const edit = (record: Partial<Item> & { idContaFinanceira: React.Key }) => {
    formManual.setFieldsValue({
      valor: record.saldo,
      juros: record.juros,
      multa: record.multa,
      outrosValores: record.outrosValores,
      desconto: record.desconto,
    });
    setEditingKey(record.idContaFinanceira);
  };

  const cancel = () => {
    setEditingKey(0);
  };

  const save = async (idContaFinanceira: React.Key) => {
    try {
      const row = (await formManual.validateFields()) as Item;
      const newData = [...dataSource];
      const index = newData.findIndex((item) => idContaFinanceira === item.idContaFinanceira);
      if (index > -1) {
        const item = newData[index];
        item.valor = realParaNumber(formManual.getFieldValue("valor"));
        item.juros = realParaNumber(formManual.getFieldValue("juros"));
        item.multa = realParaNumber(formManual.getFieldValue("multa"));
        item.outrosValores = realParaNumber(formManual.getFieldValue("outrosValores"));
        item.desconto = realParaNumber(formManual.getFieldValue("desconto"));
        item.valorTotal = item.valor + item.juros + item.multa + item.outrosValores - item.desconto;
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setDataSource(newData);
        let soma = newData.reduce((prev, current) => {
          const valorTotal = current.valorTotal ? realParaNumber(current.valorTotal.toString()) : 0;
          return prev + valorTotal;
        }, 0);
        setTotalItens(soma);
        setEditingKey(0);
      } else {
        newData.push(row);
        setDataSource(newData);
        setEditingKey(0);
      }
    } catch (e) {}
  };

  const columns = [
    {
      title: "Nº DOC",
      dataIndex: "numeroDocumento",
    },
    {
      title: "VENCIMENTO",
      dataIndex: "dataVencimento",
      render: (dataVencimento: Date) => <>{dayjs(dataVencimento).format("DD/MM/YYYY")}</>,
    },
    {
      title: "FAVORECIDO",
      dataIndex: "apelidoFavorecido",
    },
    {
      title: "SALDO A PAGAR",
      dataIndex: "valorLiquido",
      align: "right" as "right",
      render: (_: any, { saldo }: any) => currencyBRL(saldo),
    },
    {
      title: "VALOR",
      dataIndex: "valor",
      editable: true,
      align: "right" as "right",
      render: (_: any, { valor }: any) => {
        return <>{valor === null ? <></> : currencyBRL(valor)}</>;
      },
    },
    {
      title: "JUROS",
      dataIndex: "juros",
      editable: true,
      align: "right" as "right",
      render: (_: any, { juros }: any) => {
        return <>{juros === null ? <></> : currencyBRL(juros)}</>;
      },
    },
    {
      title: "MULTA",
      dataIndex: "multa",
      editable: true,
      align: "right" as "right",
      render: (_: any, { multa }: any) => {
        return <>{multa === null ? <></> : currencyBRL(multa)}</>;
      },
    },
    {
      title: "OUTROS VLR",
      dataIndex: "outrosValores",
      editable: true,
      align: "right" as "right",
      render: (_: any, { outrosValores }: any) => {
        return <>{outrosValores === null ? <></> : currencyBRL(outrosValores)}</>;
      },
    },
    {
      title: "DESCONTO",
      dataIndex: "desconto",
      editable: true,
      align: "right" as "right",
      render: (_: any, { desconto }: any) => {
        return <>{desconto === null ? <></> : currencyBRL(desconto)}</>;
      },
    },
    {
      title: "VALOR TOTAL",
      dataIndex: "valorTotal",
      align: "right" as "right",
      render: (_: any, { valorTotal }: any) => {
        return <>{valorTotal === null ? <></> : currencyBRL(valorTotal)}</>;
      },
    },
    {
      title: "HISTÓRICO",
      dataIndex: "historico",
      align: "center" as "center",
      render: (historico: string) =>
        historico !== null && (
          <Tooltip placement="top" title={historico}>
            <Button size="small">
              <FileTextOutlined />
            </Button>
          </Tooltip>
        ),
    },
    {
      title: "AÇÕES",
      render: (_: any, record: Item) => {
        const editable = isEditing(record);
        return editable ? (
          <Flex justify="center" align="center" gap={4}>
            <Tooltip title="Salvar">
              <PhosphorIcon.CheckCircle style={{ cursor: "pointer" }} size={20} color="#334155" onClick={() => save(record.idContaFinanceira)} />
            </Tooltip>
            <Tooltip title="Cancelar">
              <PhosphorIcon.XCircle style={{ cursor: "pointer" }} size={20} color="#334155" onClick={cancel} />
            </Tooltip>
          </Flex>
        ) : (
          <Flex justify="center" gap={4}>
            <Tooltip title="Editar">
              <PhosphorIcon.Pencil style={{ cursor: "pointer" }} size={20} color="#334155" onClick={() => edit(record)} />
            </Tooltip>
          </Flex>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Item) => ({
        record,
        inputType: col.dataIndex === "valor" ? "valor" : col.dataIndex === "multa" ? "multa" : col.dataIndex === "juros" ? "juros" : col.dataIndex === "outrosValores" ? "outrosValores" : "desconto",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const submit = (values: any) => {
    let detalhes: PagamentoLoteJsonModel[] = [];

    dataSource.forEach((v: any) => {
      var args = {
        idContaFinanceira: v.idContaFinanceira,
        valor: realParaNumber(v.valor),
        juros: realParaNumber(v.juros),
        multa: realParaNumber(v.multa),
        outrosValores: realParaNumber(v.outrosValores),
        desconto: realParaNumber(v.desconto),
      };

      detalhes.push(args);
    });

    let args: PagamentoLoteModel = {
      ...values,
      receitaDespesa: pagar || contaFinanceiraState.origemCadastro === "guia" || lancamentoDespesa || lancamentoDespesa ? "D" : "R",
      tipoPagamento: valueRadioTipoPagamento,
      valorTotal: totalItens,
      detalhes: detalhes,
    };

    dispatch(cadastrarPagamentoLote({ value: args, origem: contaFinanceiraState.origemCadastro }));
  };

  const voltar = () => {
    if (contaFinanceiraState.origemCadastro === "lancamento") {
      dispatch(setPageLancamentoBancario("geral"));
    } else {
      dispatch(setPageContaFinanceira("geral"));
    }
  };

  return (
    <Flex gap={12} vertical>
      <Flex onClick={voltar} className="voltar">
        {"<"} VOLTAR
      </Flex>
      <Form form={form} layout="vertical" onFinish={submit}>
        <Row gutter={12}>
          <Col span={4}>
            <Form.Item label="Conta bancária" name="idContaBanco">
              <Select
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                loading={contaBancoState.loading}
                options={contaBancoState.list.map((a: Vm_ContaBancoModel) => ({
                  value: a.id,
                  label: a.denominacaoPersonalizada,
                  disabled: !a.ativo,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Forma pagamento"
              name="idFormaPagamentoRecebimento"
              rules={[
                {
                  required: true,
                  message: "Campo obrigatório",
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Selecione uma forma de pagamento"
                style={{ width: "100%" }}
                loading={formaPagamentoRecebimentoState.loading}
                options={formaPagamentoRecebimentoState.list.map((a: any) => ({
                  value: a.id,
                  label: a.denominacao,
                  disabled: !a.ativo,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Data do pagamento"
              name="data"
              rules={[
                {
                  required: true,
                  message: "Campo obrigatório",
                },
              ]}
            >
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Tipo">
              <Radio.Group
                style={{ width: "100%" }}
                options={radioOptionsTipoPagamento}
                onChange={onChangeRadioTipoPagamento}
                value={valueRadioTipoPagamento}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
          </Col>
          <Col span={2}></Col>
          <Col span={3}>
            <Form.Item label="Valor total">
              <NumericFormat
                className="ant-input noBoderColor number-input"
                disabled
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                value={totalItens}
                style={{ width: "130px" }}
              />
            </Form.Item>
          </Col>
          <Col span={3} className="cadastrarCol">
            <Form.Item label="Saldo ">
              <NumericFormat
                className="ant-input noBoderColor number-input"
                disabled
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                value={totalSaldo}
                style={{ width: "130px" }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form form={formManual} component={false}>
          <Form.Item>
            <Row gutter={12}>
              <Col span={24}>
                <Table
                  size="small"
                  components={{
                    body: {
                      cell: EditableCell,
                    },
                  }}
                  rowClassName="editable-row"
                  dataSource={dataSource}
                  columns={mergedColumns}
                  pagination={false}
                  loading={contaFinanceiraState.loading}
                  scroll={{ x: 809 }}
                />
              </Col>
            </Row>
          </Form.Item>
        </Form>
        <Divider />
        <Row gutter={12}>
          <Col span={24} className="modalFooter">
            <Space size="middle">
              <Button className="cancelButton" onClick={voltar}>
                FECHAR
              </Button>
              <Button htmlType="submit" icon={<CheckOutlined />} className="buttonPrimary">
                SALVAR
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Flex>
  );
};

export default PagamentosPage;
